import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { AccountSummary, AssetSummary, IdentitySummary, MEDIA_FORMATS, ProjectRow } from "../../../../../database/mediaOps/mediaOpsApi";
import ProjectDetail from "./ProjectDetail";
import { useMutation, useOpRead } from "./hooks";
import { modeLabel } from "./stateLabels";
import { FieldLabel, FormGrid, Grow, Hint, ListRow, Mono, Panel, PanelHead, ResultNotice, Row, Select, Small, TextArea, TextInput } from "./ui";

// Projects: create from eligible sources, then open one to revise, render,
// review per target and schedule.

export const ProjectsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loadError, setLoadError] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => {
    mediaOps.listProjects(communityId).then((r) => {
      if (r.status === "blocked") {
        setLoadError(r.errors.map((e) => e.message).join(" ") || "Could not load projects.");
        return;
      }
      setRows(r.data?.projects || []);
      setLoadError("");
    });
  }, [communityId]);
  useEffect(load, [load]);

  if (openId) {
    return (
      <>
        <Row>
          <Button size="sm" variant="ghost" onClick={() => setOpenId(null)}>
            Back to projects
          </Button>
        </Row>
        <ProjectDetail communityId={communityId} projectId={openId} />
      </>
    );
  }

  return (
    <>
      <Panel>
        <PanelHead>
          Projects <Grow />
          <Button size="sm" variant="secondary" onClick={load}>
            Refresh
          </Button>
          <Button size="sm" onClick={() => setCreating((v) => !v)}>
            {creating ? "Close" : "New project"}
          </Button>
        </PanelHead>
        {loadError ? <Small>{loadError}</Small> : null}
        {rows.length === 0 && !loadError ? <Small>No pipeline projects yet.</Small> : null}
        {rows.map((p) => (
          <ListRow key={p.projectId}>
            <strong>{p.title || p.projectId}</strong>
            <Tag size="sm">{p.format}</Tag>
            {p.review?.status ? <Tag size="sm">{p.review.status}</Tag> : null}
            <Small>{(p.targets || []).length} targets</Small>
            <Small>Revision {p.currentRevisionId}</Small>
            <Grow />
            <Button size="sm" variant="secondary" onClick={() => setOpenId(p.projectId)}>
              Open
            </Button>
          </ListRow>
        ))}
      </Panel>
      {creating ? (
        <CreateProjectForm
          communityId={communityId}
          onCreated={(pid) => {
            setCreating(false);
            load();
            setOpenId(pid);
          }}
        />
      ) : null}
    </>
  );
};

const CreateProjectForm: React.FC<{ communityId: string; onCreated: (projectId: string) => void }> = ({ communityId, onCreated }) => {
  const accountsRead = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const identitiesRead = useOpRead(() => mediaOps.listIdentities(communityId), [communityId]);
  const accounts: AccountSummary[] = accountsRead.result?.data?.accounts || [];
  const identities: IdentitySummary[] = identitiesRead.result?.data?.identities || [];
  const [title, setTitle] = useState("");
  const [identityId, setIdentityId] = useState("");
  const [format, setFormat] = useState<string>("slideshow");
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [sources, setSources] = useState<string[]>([]);
  const [eligible, setEligible] = useState<AssetSummary[] | null>(null);
  const [eligibleNote, setEligibleNote] = useState("");
  const m = useMutation<{ projectId?: string }>();
  const edit = () => m.edited();

  const targetIds = useMemo(() => Object.keys(targets).sort(), [targets]);
  const identityAccounts = accounts.filter((a) => !identityId || a.identityId === identityId);

  // Sources must be eligible for every chosen target: intersect per account.
  useEffect(() => {
    let alive = true;
    if (targetIds.length === 0) {
      setEligible(null);
      return undefined;
    }
    Promise.all(targetIds.map((aid) => mediaOps.listAssets(communityId, { eligibleForAccountId: aid }))).then((results) => {
      if (!alive) return;
      const blocked = results.find((r) => r.status === "blocked");
      if (blocked) {
        setEligible([]);
        setEligibleNote(blocked.errors.map((e) => `${e.code}: ${e.message}`).join(" "));
        return;
      }
      const sets = results.map((r) => new Set((r.data?.assets || []).map((a: AssetSummary) => a.id)));
      const first = (results[0].data?.assets || []) as AssetSummary[];
      setEligible(first.filter((a) => sets.every((s) => s.has(a.id))));
      setEligibleNote("");
    });
    return () => {
      alive = false;
    };
  }, [communityId, targetIds]);

  // Drop chosen sources that are no longer eligible for the chosen targets.
  useEffect(() => {
    if (!eligible) return;
    const ok = new Set(eligible.map((a) => a.id));
    setSources((prev) => prev.filter((id) => ok.has(id)));
  }, [eligible]);

  const toggleTarget = (a: AccountSummary) => {
    edit();
    setTargets((prev) => {
      const next = { ...prev };
      if (next[a.id]) delete next[a.id];
      else next[a.id] = a.defaultMode || a.allowedModes[0] || "owner_email";
      return next;
    });
  };
  const move = (i: number, dir: -1 | 1) => {
    edit();
    setSources((prev) => {
      const next = prev.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.createProject(k, communityId, {
        title: title.trim(),
        identityId,
        format,
        sourceAssetIds: sources,
        captionsByAccount: Object.fromEntries(targetIds.map((aid) => [aid, captions[aid] || ""])),
        targets: targetIds.map((aid) => ({ accountId: aid, deliveryMode: targets[aid] })),
      })
    );
    if (res && res.status !== "blocked" && res.data?.projectId) onCreated(res.data.projectId);
  };

  const ready = title.trim() && identityId && targetIds.length > 0 && sources.length > 0 && targetIds.every((aid) => (captions[aid] || "").trim());

  return (
    <Panel>
      <PanelHead>New project</PanelHead>
      <FormGrid>
        <FieldLabel>
          Title
          <TextInput
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              edit();
            }}
          />
        </FieldLabel>
        <FieldLabel>
          Identity
          <Select
            value={identityId}
            onChange={(e) => {
              setIdentityId(e.target.value);
              setTargets({});
              edit();
            }}
          >
            <option value="">Choose an identity</option>
            {identities.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.kind})
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Format
          <Select
            value={format}
            onChange={(e) => {
              setFormat(e.target.value);
              edit();
            }}
          >
            {MEDIA_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </FormGrid>

      <div>
        <Small>Choose an account and how you want to publish this post.</Small>
        {identityAccounts.length === 0 ? <Hint>No accounts for this identity.</Hint> : null}
        {identityAccounts.map((a) => (
          <ListRow key={a.id} $active={!!targets[a.id]} style={{ marginTop: 6 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={!!targets[a.id]} onChange={() => toggleTarget(a)} aria-label={`Target @${a.handle}`} />
              <strong>@{a.handle}</strong>
            </label>
            <Tag size="sm">{a.platform}</Tag>
            {targets[a.id] ? (
              <Select
                style={{ width: "auto" }}
                aria-label={`Publishing method for @${a.handle}`}
                value={targets[a.id]}
                onChange={(e) => {
                  const v = e.target.value;
                  setTargets((prev) => ({ ...prev, [a.id]: v }));
                  edit();
                }}
              >
                {a.allowedModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {modeLabel(mode)}
                  </option>
                ))}
              </Select>
            ) : null}
            {targets[a.id] === "auto_publish" ? (
              <Small>Tribed publishes the approved post to this account at the scheduled time.</Small>
            ) : targets[a.id] === "owner_email" ? (
              <Small>Email the approved media and caption to {a.ownerMasked || "the account owner"}. You publish it yourself; email delivery does not mark it as published.</Small>
            ) : <Small>Owner: {a.ownerMasked || "not set"}</Small>}
          </ListRow>
        ))}
      </div>

      {targetIds.map((aid) => {
        const a = accounts.find((x) => x.id === aid);
        return (
          <FieldLabel key={aid}>
            Caption for @{a?.handle || aid}
            <TextArea
              value={captions[aid] || ""}
              onChange={(e) => {
                const v = e.target.value;
                setCaptions((prev) => ({ ...prev, [aid]: v }));
                edit();
              }}
            />
          </FieldLabel>
        );
      })}

      <div>
        <Small>Sources in order. Only assets eligible for every chosen target are listed.</Small>
        {eligible === null ? <Hint>Choose target accounts first.</Hint> : null}
        {eligibleNote ? <Small>{eligibleNote}</Small> : null}
        {sources.map((id, i) => (
          <ListRow key={id} style={{ marginTop: 6 }}>
            <Small>{i + 1}.</Small>
            <Mono>{id}</Mono>
            <Grow />
            <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${id} up`}>
              Up
            </Button>
            <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === sources.length - 1} aria-label={`Move ${id} down`}>
              Down
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSources((prev) => prev.filter((x) => x !== id));
                edit();
              }}
            >
              Remove
            </Button>
          </ListRow>
        ))}
        {(eligible || [])
          .filter((a) => !sources.includes(a.id))
          .map((a) => (
            <ListRow key={a.id} style={{ marginTop: 6 }}>
              <Tag size="sm" variant="success">
                Eligible
              </Tag>
              <Tag size="sm">{a.type}</Tag>
              <Mono>{a.id}</Mono>
              <Grow />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setSources((prev) => [...prev, a.id]);
                  edit();
                }}
              >
                Add
              </Button>
            </ListRow>
          ))}
      </div>
      <Row>
        <Button size="sm" onClick={submit} disabled={!ready} isLoading={m.busy}>
          Create project
        </Button>
        {!ready ? <Small>Needs a title, identity, targets with captions and at least one source.</Small> : null}
      </Row>
      <ResultNotice result={m.result} okText="Project created as a draft revision. Nothing was sent." />
    </Panel>
  );
};

export default ProjectsView;
